import React from 'react';
import cn from 'clsx';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { Table, Button } from 'antd';
import ColumnActions from 'components/Antd/Table/ColumnActions';
import { ATooltip } from 'components/Antd';
import { ReactComponent as AttachmentSvg } from 'assets/icons/New/Attachment.svg';
import { ReactComponent as DuplicateSvg } from 'assets/icons/New/Duplicate.svg';
import { ReactComponent as RenewSvg } from 'assets/icons/New/Renew.svg';
import { ReactComponent as ArchiveSvg } from 'assets/icons/New/Archive.svg';
import { ReactComponent as UnarchiveSvg } from 'assets/icons/New/Unarchive.svg';
import { ReactComponent as EditSvg } from 'assets/icons/New/Edit.svg';
import { ReactComponent as Trash1Svg } from 'assets/icons/New/Trash1.svg';
import { getMomentDate } from 'util/date';
import classes from './ComplianceTable.module.scss';
import CellFactory from 'components/Molecules/Table/Cells/CellFactory';

export const RESULT_LABEL = {
  passed: 'Passed',
  pass: 'Passed',
  rejected: 'Reject',
  pending: 'Pending',
  failed: 'Failed',
  fail: 'Failed',
  reportAsIs: 'Report As Is',
  default: '',
};

export const rowIsExpand = (record) => record?.subs?.length > 0;
export const rowIsArchived = (record) => record?.status === 'archived';
export const rowHasDocument = (record) => record?.documentLists?.length > 0;
export const renderNormal = (tKey, width) => (_, obj) => (
  <span
    style={{
      display: 'block',
      width: width ? `${width}px` : undefined,
    }}>
    {get(obj, tKey) || '/'}
  </span>
);
export const renderCitation = (val, obj) => {
  return (
    <span className={classes.citationInner}>
      <span
        style={{
          wordBreak: 'break-word',
        }}>
        {val?.name || '/'}
      </span>
    </span>
  );
};
export const renderTestItem = (val, obj) => {
  return (
    <span className={classes.testItemInner}>
      <span
        style={{
          wordBreak: 'break-word',
        }}>
        {val?.name || '/'}
      </span>
      {obj.isNewFlag && <span className={classes.tag}>New</span>}
    </span>
  );
};
export const renderDescription = (val, obj) => {
  return (
    <span className={classes.testItemInner}>
      <span
        style={{
          wordBreak: 'break-word',
        }}>
        {val || '/'}
      </span>
      {obj.isNewFlag && <span className={classes.tag}>New</span>}
    </span>
  );
};
export const renderDate = (val) => {
  const today = getMomentDate('', true).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const tMomentVal = getMomentDate(val);
  const isExpired =
    !!tMomentVal &&
    tMomentVal
      .clone()
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .isBefore(today);
  return (
    <ATooltip
      dark
      title={isExpired ? 'Expired' : ''}
      placement="top"
      overlayClassName={classes.tooltipOverlay}>
      <span
        className={cn(classes.dateInner, {
          [classes.isRed]: isExpired,
        })}>
        {!!tMomentVal ? tMomentVal.format('DD MMM YYYY') : '/'}
      </span>
    </ATooltip>
  );
};
export const renderResult = (val, isOther) => {
  return (
    <span
      className={cn(classes.resultInner, {
        [classes.isSuccess]: val === 'passed' || val === 'pass',
        [classes.isFailed]: val === 'failed' || val === 'fail',
      })}>
      <span>{val === 'reportAsIs' && isOther ? 'N/A' : RESULT_LABEL[val] || '/'}</span>
    </span>
  );
};
export const renderAction = ({
  onArchive,
  onDelete,
  onEdit,
  onRenew,
  onDuplicate,
  onUnarchive,
  onUpdateDocument,
}) => (_, record) => {
  const hasDocument = rowHasDocument(record);
  const tDocumentName = record?.documentLists?.[0]?.fileName;
  const isArchived = rowIsArchived(record);
  return (
    <span className={classes.actionInner}>
      {hasDocument ? (
        <ATooltip
          zIndex={100}
          dark
          // title={<span className={classes.fileName}>{tDocumentName}</span>}
          placement="top"
          trigger={['click']}
          overlayClassName={classes.tooltipOverlay}
          title={
            <div className={classes.documentList}>
              {record.documentLists.map((doc) => (
                <div key={doc.id}>
                  {CellFactory('QMASDocumentModalCell', {
                    wrapClassName: classes.overlayModal,
                    documents: [
                      {
                        ...doc,
                        name: doc.fileName,
                        ...doc.documentListIndex,
                        documentListId: doc.id,
                      },
                    ],
                    triggerElement: (
                      <Button className={classes.documentItem} type="text">
                        {doc.fileName}
                      </Button>
                    ),
                    onSubmit: (values, form, stateReadonly, cb) => {
                      if (!stateReadonly) {
                        onUpdateDocument(values, form, cb);
                      }
                    },
                  })}
                </div>
              ))}
            </div>
          }>
          <span className={classes.attachmentIcon}>
            <AttachmentSvg />
          </span>
        </ATooltip>
      ) : (
        <span style={{ display: 'block', width: '16px', height: '16px' }}></span>
      )}
      <ColumnActions
        color="default"
        dropdownOverlay={cn(classes.dropdownOverlay, {
          [classes.dropdownOverlaySub]: !hasDocument && !isArchived,
        })}
        items={
          isArchived
            ? [
                {
                  key: '4',
                  label: (
                    <>
                      <UnarchiveSvg />
                      <span>Unarchive</span>
                    </>
                  ),
                  onClick: (e) => {
                    if (onUnarchive) onUnarchive(record);
                  },
                },
              ]
            : hasDocument
            ? [
                {
                  key: '2',
                  label: (
                    <>
                      <DuplicateSvg />
                      <span>Duplicate</span>
                    </>
                  ),
                  onClick: (e) => {
                    if (onDuplicate) onDuplicate(record);
                  },
                },
                {
                  key: '3',
                  label: (
                    <>
                      <RenewSvg />
                      <span>Renew</span>
                    </>
                  ),
                  onClick: (e) => {
                    if (onRenew) onRenew(record);
                  },
                },
                {
                  key: '4',
                  label: (
                    <>
                      {isArchived ? <UnarchiveSvg /> : <ArchiveSvg />}
                      <span>{isArchived ? 'Unarchive' : 'Archive'}</span>
                    </>
                  ),
                  onClick: (e) => {
                    if (isArchived) {
                      if (onUnarchive) onUnarchive(record);
                    } else {
                      if (onArchive) onArchive(record);
                    }
                  },
                },
              ]
            : [
                {
                  key: '1',
                  label: (
                    <>
                      <EditSvg />
                      <span>Edit</span>
                    </>
                  ),
                  onClick: (e) => {
                    if (onEdit) onEdit(record);
                  },
                },
                {
                  key: '2',
                  label: (
                    <>
                      <DuplicateSvg />
                      <span>Duplicate</span>
                    </>
                  ),
                  onClick: (e) => {
                    if (onDuplicate) onDuplicate(record);
                  },
                },
                {
                  key: '3',
                  label: (
                    <>
                      <RenewSvg />
                      <span>Renew</span>
                    </>
                  ),
                  onClick: (e) => {
                    if (onRenew) onRenew(record);
                  },
                },
                {
                  key: '4',
                  label: (
                    <>
                      {rowIsArchived(record) ? <UnarchiveSvg /> : <ArchiveSvg />}
                      <span>{rowIsArchived(record) ? 'Unarchive' : 'Archive'}</span>
                    </>
                  ),
                  onClick: (e) => {
                    if (rowIsArchived(record)) {
                      if (onUnarchive) onUnarchive(record);
                    } else {
                      if (onArchive) onArchive(record);
                    }
                  },
                },
                {
                  key: '5',
                  label: (
                    <>
                      <Trash1Svg />
                      <span>Delete</span>
                    </>
                  ),
                  onClick: (e) => {
                    if (onDelete) onDelete(record);
                  },
                },
              ]
        }
      />
    </span>
  );
};

export const renderNotHighLight = (val) => {
  const tMomentVal = getMomentDate(val);

  return (
    <span className={cn(classes.dateInner)}>
      {!!tMomentVal ? tMomentVal.format('DD MMM YYYY') : '/'}
    </span>
  );
};

export const getColumns = ({
  onArchive,
  onDelete,
  onEdit,
  onRenew,
  onDuplicate,
  onUnarchive,
  onUpdateDocument,
  type,
  isSub,
}) => {
  const isTest = type === 'test';
  const isCertificate = type === 'certificate';
  const columns = [
    ...(isTest
      ? [
          {
            title: 'Export Market',
            dataIndex: 'countryList',
            key: 'countryList',
            render: renderNormal('countryList.name', isSub ? 110 : 130),
            width: 162,
          },
          {
            title: 'Test Item',
            dataIndex: 'testItem',
            key: 'testItem',
            render: (val) => val?.name || '/',
            // render: renderNormal('testItem.name'),
            render: renderTestItem,
          },
          {
            title: 'Citation',
            dataIndex: 'citation',
            key: 'citation',
            render: renderCitation,
            width: 200,
          },
          {
            title: 'Report ID',
            dataIndex: 'reportId',
            key: 'reportId',
            render: renderNormal('reportId', 80),
            width: 112,
          },
          {
            title: 'Issue Party',
            dataIndex: 'serviceProvider',
            key: 'serviceProvider',
            render: renderNormal('serviceProvider.name', 80),
            width: 112,
          },
          {
            title: 'Expiry Date',
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            render: renderDate,
            width: 120,
          },
          {
            title: 'Test Date / Report Date',
            dataIndex: 'testDate',
            key: 'testDate',
            render: renderNotHighLight,
            width: 120,
          },
          {
            title: 'Result',
            dataIndex: 'result',
            key: 'result',
            render: (val) => renderResult(val, false),
            width: 100,
          },
        ]
      : isCertificate
      ? [
          {
            title: 'Type',
            dataIndex: 'certificateType',
            key: 'certificateType',
            // render: renderNormal('certificateType.name'),
            render: renderTestItem,
          },
          {
            title: 'Score / Overall Rating',
            dataIndex: 'customCertificateScore',
            key: 'customCertificateScore',
            render: renderNormal('customCertificateScore.name', 130),
            width: 162,
          },
          {
            title: 'Issue Party',
            dataIndex: 'customIssueParty',
            key: 'customIssueParty',
            render: renderNormal('customIssueParty.name', 100),
            width: 132,
          },
          {
            title: 'Expiry Date',
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            render: renderDate,
            width: 120,
          },
          {
            title: 'Certificate Date / Report Date',
            dataIndex: 'testDate',
            key: 'testDate',
            render: renderNotHighLight,
            width: 120,
          },
        ]
      : [
          {
            title: 'Type',
            dataIndex: 'customComplianceOtherDataType',
            key: 'customComplianceOtherDataType',
            render: renderNormal('customComplianceOtherDataType.name', 170),
            width: 202,
          },
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: renderNormal('name', 130),
            width: 162,
          },
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            // render: renderNormal('description', 130),
            render: renderDescription,
            // width: 162,
          },
          {
            title: 'Issue Party',
            dataIndex: 'customIssueParty',
            key: 'customIssueParty',
            render: renderNormal('customIssueParty.name', 100),
            width: 132,
          },
          {
            title: 'Expiry Date',
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            render: renderDate,
            width: 120,
          },
          {
            title: 'Issue Date',
            dataIndex: 'testDate',
            key: 'testDate',
            render: renderNotHighLight,
            width: 120,
          },
          {
            title: 'Result',
            dataIndex: 'result',
            key: 'result',
            render: (val) => renderResult(val, true),
            width: 100,
          },
        ]),
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: renderAction({
        onArchive,
        onDelete,
        onEdit,
        onRenew,
        onDuplicate,
        onUnarchive,
        onUpdateDocument,
      }),
      width: 50,
      className: 'ant-column-action',
    },
    Table.EXPAND_COLUMN,
  ];
  return columns;
};

export const filteredData = (data, type) => {
  const tData = orderBy(
    data || [],
    [
      (item) => (item.status === 'archived' ? 1 : 0),
      'status',
      (item) => new Date(item.expiryDate).getTime(),
    ],
    ['asc', 'asc', 'desc']
  );
  if (tData.length === 0) return [];
  if (!!type) {
    const testDataGrouped = groupBy(tData, (item) =>
      type === 'test'
        ? `"${item.countryListId}+${item.testItemId}"`
        : type === 'certificate'
        ? item.certificateTypeId
        : item.customComplianceOtherDataTypeId
    );
    const returnData = Object.keys(testDataGrouped).map((key) => {
      const tList = orderBy(
        testDataGrouped[key],
        [
          (item) => (item.status === 'archived' ? 1 : 0),
          'status',
          (item) => new Date(item.expiryDate).getTime(),
        ],
        ['asc', 'asc', 'desc']
      );
      return {
        ...tList[0],
        subs: tList.filter((_, index) => index > 0),
      };
    });
    return returnData;
  }
  return tData;
};
